// The switch at the top of a merged app (2026-09-24): the Calendar is a list,
// a day by the hour, a month and Recurring, Review carries Someday, Notes
// is pages and lists, Library is music and the rest, History is done, deleted,
// the system's trail and old capture receipts. Each tab is its own
// route, so links, pushes and the back button keep working.
import { Link, useRouterState } from '@tanstack/react-router'
import { useAccess } from '@/state/access'
import { cx } from './ui'

type Tab = {
  to: string
  label: string
  /** the tab is one page told apart by its search, not by its path */
  search?: Record<string, string>
  /** overrides the path test */
  on?: boolean
  /** on only at its own path, not under it (a pack's first page whose others sit beneath it) */
  exact?: boolean
}

export const AHEAD_TABS: Tab[] = [
  { to: '/upcoming', label: 'List' },
  { to: '/day', label: 'Day' },
  { to: '/calendar', label: 'Month' },
  { to: '/recurring', label: 'Recurring' },
]
export const REVIEW_TABS: Tab[] = [
  { to: '/review', label: 'This week' },
  { to: '/plan', label: 'Plan' },
  { to: '/someday', label: 'Someday' },
]
export const NOTES_TABS: Tab[] = [
  { to: '/pages', label: 'Pages' },
  { to: '/lists', label: 'Lists' },
]
export const LIBRARY_TABS: Tab[] = [
  { to: '/music', label: 'Music' },
  { to: '/library', label: 'Films, TV and games' },
]
// the infra pack (2026-10-31): three sidebar entries, one switch on the phone
export const INFRA_TABS: Tab[] = [
  { to: '/infra', label: 'Overview', exact: true },
  { to: '/infra/hosts', label: 'Hosts' },
  { to: '/infra/storage', label: 'Storage' },
]
export const HISTORY_TABS: Tab[] = [
  { to: '/log', label: 'Done' },
  { to: '/trash', label: 'Deleted' },
  { to: '/activity', label: 'System' },
  // receipts of captures Prime filed by itself, before 2026-09-24; new ones wait in the Inbox
  { to: '/captures', label: 'Captures' },
]

/** Tabs that read what only the owner has: the audit trail, filed captures, the library. */
const OWNER_ONLY = ['/activity', '/captures', '/music', '/library']

export const AppTabs = ({ tabs: all, label }: { tabs: Tab[]; label: string }) => {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const member = useAccess((s) => s.member)
  const tabs = member ? all.filter((t) => !OWNER_ONLY.includes(t.to)) : all
  return (
    <nav
      aria-label={label}
      className="inline-flex max-w-full overflow-x-auto scrollbar-none rounded-full bg-raised border border-line p-0.5 mb-4"
      data-testid="app-tabs"
    >
      {tabs.map((t) => {
        const on = t.on ?? (path === t.to || (!t.exact && path.startsWith(`${t.to}/`)))
        return (
          <Link
            key={t.to + (t.search?.tab ?? '')}
            to={t.to}
            search={t.search as never}
            replace
            aria-current={on ? 'page' : undefined}
            className={cx(
              'shrink-0 rounded-full px-3.5 min-h-8 inline-flex items-center text-[13px] font-medium whitespace-nowrap transition-colors',
              on ? 'bg-accent text-on-accent' : 'text-muted hover:text-fg',
            )}
          >
            {t.label}
          </Link>
        )
      })}
    </nav>
  )
}
